<template>
  <v-container>
    <v-tab-item class="pb-12">
      <div class="aba-inicio-wrap">
        <div class="display-1 font-weight-normal mb-6 text-center">Vamos começar preenchendo as informações básicas</div>
        <aba-inicio
          ref="abaInicio"
          v-model="inicio"
          :esquemas="esquemas"
          :templates="templates"
          :tipo-padrao="tipoPadrao"
          @input="refreshAvailability"
          @hook:mounted="$emit('inicio-montado')"
        />
      </div>
    </v-tab-item>

    <v-tab-item class="pb-12">
      <mapeamento-base
        ref="abaPartida"
        exige-quantidade
        :uploading="uploadingPartida"
        :initial-colunas="bases.PARTIDA.colunas"
        :initial-campos="bases.PARTIDA.campos"
        :initial-profiling-status="bases.PARTIDA.profilingStatus"
        :initial-row-count="bases.PARTIDA.rowCount"
        :initial-arquivo-origem="bases.PARTIDA.arquivoOrigem"
        :initial-nome-base="bases.PARTIDA.nomeBase"
        :initial-encoding="bases.PARTIDA.encoding"
        :initial-delimitador="bases.PARTIDA.delimitador"
        :initial-arquivo-interno-zip="bases.PARTIDA.arquivoInternoZip"
        :esquemas="esquemas"
        @upload="$emit('upload-partida', $event)"
        @mapping-change="refreshAvailability"
      />
    </v-tab-item>

    <v-tab-item class="pb-12">
      <mapeamento-base
        ref="abaReferencia"
        exige-quantidade
        show-sugestao
        :uploading="uploadingReferencia"
        :loading-sugestao="loadingSugestao"
        :initial-colunas="bases.REFERENCIA.colunas"
        :initial-campos="bases.REFERENCIA.campos"
        :initial-profiling-status="bases.REFERENCIA.profilingStatus"
        :initial-row-count="bases.REFERENCIA.rowCount"
        :initial-arquivo-origem="bases.REFERENCIA.arquivoOrigem"
        :initial-nome-base="bases.REFERENCIA.nomeBase"
        :initial-encoding="bases.REFERENCIA.encoding"
        :initial-delimitador="bases.REFERENCIA.delimitador"
        :initial-arquivo-interno-zip="bases.REFERENCIA.arquivoInternoZip"
        :esquemas="esquemas"
        :campos-partida="bases.PARTIDA.campos"
        @upload="$emit('upload-referencia', $event)"
        @sugerir="$emit('aplicar-sugestao')"
        @mapping-change="refreshAvailability"
      />
    </v-tab-item>

    <v-tab-item class="pb-12">
      <aba-rodadas
        ref="abaRodadas"
        :value="rodadas"
        :atributos="atributosRodadas"
        :loading="loadingRodadas"
        :has-more="rodadasHasMore"
        :has-prev="rodadasHasPrev"
        :page-index="rodadasPageIndex"
        :total="rodadasTotal"
        :campos-config="rodadasCamposConfig"
        :estado-por-campo="rodadasEstadoPorCampo"
        :tipo-ordenacao-config="rodadasTipoOrdenacao"
        @pagina-anterior="$emit('rodadas-pagina-anterior')"
        @pagina-proxima="$emit('rodadas-pagina-proxima')"
        @prioridade-change="$emit('rodadas-prioridade-change', $event)"
        @mover="$emit('rodadas-mover', $event)"
        @editar-campos="$emit('rodadas-editar-campos', $event)"
        @adicionar="$emit('rodadas-adicionar', $event)"
        @duplicar="$emit('rodadas-duplicar', $event)"
        @remover="$emit('rodadas-remover', $event)"
      />
    </v-tab-item>
  </v-container>
</template>

<script>
  const emptyBase = () => ({
    colunas: [],
    campos: [],
    profilingStatus: null,
    rowCount: null,
    nomeBase: null,
    arquivoOrigem: null,
    encoding: null,
    delimitador: null,
    arquivoInternoZip: null
  });

  export default {
    name: 'ConciliacaoWizardTabs',
    components: {
      AbaInicio: () => import('@/components/conciliacoesV3/AbaInicio.vue'),
      MapeamentoBase: () => import('@/components/conciliacoesV3/MapeamentoBase.vue'),
      AbaRodadas: () => import('@/components/conciliacoesV3/AbaRodadas.vue')
    },
    props: {
      entity: { type: Object, default: null },
      uploadingPartida: { type: Boolean, default: false },
      uploadingReferencia: { type: Boolean, default: false },
      loadingSugestao: { type: Boolean, default: false },
      loadingRodadas: { type: Boolean, default: false },
      rodadasHasMore: { type: Boolean, default: false },
      rodadasHasPrev: { type: Boolean, default: false },
      rodadasPageIndex: { type: Number, default: 0 },
      rodadasTotal: { type: Number, default: null },
      rodadas: { type: Array, default: () => [] },
      rodadasCamposConfig: { type: Array, default: () => [] },
      rodadasEstadoPorCampo: { type: Object, default: () => ({}) },
      rodadasTipoOrdenacao: { type: String, default: null },
      esquemas: { type: Object, default: () => ({}) },
      templates: { type: Array, default: () => [] },
      tipoPadrao: { type: String, default: 'STANDARD' }
    },
    data: () => ({
      inicio: {
        nome: '',
        tipo: 'STANDARD',
        flexibilizacao: { partida: null, referencia: null },
        coringa: { habilitado: false, nivelCoringa: null }
      },
      bases: {
        PARTIDA: emptyBase(),
        REFERENCIA: emptyBase()
      }
    }),
    computed: {
      atributosRodadas() {
        const campos = this.bases.PARTIDA.campos || [];
        return campos
          .filter((c) => c && c.id)
          .slice()
          .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          .map((c) => ({
            id: String(c.id),
            nome: c.nomeExibicao || c.colunaOrigem || String(c.id)
          }));
      }
    },
    watch: {
      entity: {
        immediate: true,
        deep: true,
        handler(entity) {
          if (!entity) {
            this.refreshAvailability();
            return;
          }
          const params = entity.parametros || {};
          this.inicio = {
            nome: entity.nome || '',
            tipo: entity.tipo || this.tipoPadrao || 'STANDARD',
            flexibilizacao: params.flexibilizacao || { partida: null, referencia: null },
            coringa: params.coringa || { habilitado: false, nivelCoringa: null }
          };
          this.bases.PARTIDA = this.hidratarBase(params.bases?.PARTIDA);
          this.bases.REFERENCIA = this.hidratarBase(params.bases?.REFERENCIA);
          this.refreshAvailability();
        }
      },
      rodadas: {
        deep: true,
        handler() {
          this.refreshAvailability();
        }
      }
    },
    mounted() {
      this.refreshAvailability();
    },
    methods: {
      hidratarBase(base) {
        return {
          colunas: base?.colunas || [],
          campos: base?.campos || [],
          profilingStatus: base?.profilingStatus || null,
          rowCount: base?.rowCount ?? null,
          nomeBase: base?.nomeBase || null,
          arquivoOrigem: base?.arquivoOrigem || null,
          encoding: base?.encoding || null,
          delimitador: base?.delimitador || null,
          arquivoInternoZip: base?.arquivoInternoZip || null
        };
      },
      refreshAvailability() {
        this.$nextTick(() => {
          const entity = this.entity || {};
          const inicioOk = this.$refs.abaInicio?.validate?.() === true || !!entity.inicio_ok;
          const partidaOk = this.$refs.abaPartida?.validate?.() === true || !!entity.partida_ok;
          const referenciaOk = this.$refs.abaReferencia?.validate?.() === true || !!entity.referencia_ok;
          const rodadasOk = this.$refs.abaRodadas?.validate?.() === true || !!entity.rodadas_ok;

          this.$emit('inicioTabAvailable', true);
          this.$emit('partidaTabAvailable', inicioOk || partidaOk || referenciaOk || rodadasOk);
          this.$emit('referenciaTabAvailable', partidaOk || referenciaOk || rodadasOk);
          this.$emit('rodadasTabAvailable', referenciaOk || rodadasOk);
          this.$emit('btnFinishAvailable', rodadasOk ? undefined : false);
        });
      },
      applyUploadPartida(upload) {
        this.$refs.abaPartida?.applyUploadResult(upload);
        this.refreshAvailability();
      },
      applyUploadReferencia(upload) {
        this.$refs.abaReferencia?.applyUploadResult(upload);
        this.refreshAvailability();
      },
      aplicarSugestoesReferencia(sugestoes) {
        return this.$refs.abaReferencia ? this.$refs.abaReferencia.aplicarSugestoes(sugestoes) : 0;
      },
      applyProfiling(papel, payload) {
        const ref = String(papel).toUpperCase() === 'REFERENCIA' ? this.$refs.abaReferencia : this.$refs.abaPartida;
        ref?.applyProfiling(payload);
        const alvo = this.bases[String(papel).toUpperCase()];
        if (alvo) {
          alvo.profilingStatus = payload?.profilingStatus || alvo.profilingStatus;
          if (payload?.rowCount != null) alvo.rowCount = payload.rowCount;
        }
      },
      validateInicio() {
        return this.$refs.abaInicio.validate();
      },
      getInicioPayload() {
        return this.$refs.abaInicio?.getPayload();
      },
      validatePartida() {
        return this.$refs.abaPartida.validate();
      },
      getPartidaPayload() {
        return this.$refs.abaPartida.getPayload();
      },
      validateReferencia() {
        return this.$refs.abaReferencia.validate();
      },
      getReferenciaPayload() {
        return this.$refs.abaReferencia.getPayload();
      },
      validateRodadas() {
        return this.$refs.abaRodadas.validate();
      },
      getRodadasPayload() {
        return this.$refs.abaRodadas.getPayload();
      },
      prepararCamposRodada(campos) {
        return this.$refs.abaRodadas ? this.$refs.abaRodadas.prepararCampos(campos) : { campos, estadoPorCampo: {} };
      },
      getRodadasGeracaoPayload() {
        return this.$refs.abaRodadas.getGeracaoPayload();
      }
    }
  };
</script>

<style scoped>
  .aba-inicio-wrap {
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;
  }
</style>
